/**
 * /flpdiff — open-source CLI + web viewer page.
 *
 * Marketing-allowed-to-be-technical companion to the main landing.
 * Hosts the live drop-zone inline (`#dropzone-slot` etc.) so producers
 * can try the diff without leaving the page. Marketing chrome is
 * mounted once; viewer slots get repainted on state changes by the
 * subscribe listener in main.ts.
 */

import { h } from "./ui.ts";
import { renderNav, renderFooter, GITHUB_URL } from "./page-shared.ts";

const NPM_URL = "https://www.npmjs.com/package/flpdiff";

export function renderFlpdiffPage(): HTMLElement {
  return h("div", { class: "flpdiff-page" }, [
    renderNav("flpdiff"),
    renderHero(),
    renderViewerEmbed(),
    renderQuickstart(),
    renderExampleOutput(),
    renderGitIntegration(),
    renderPluginMatrix(),
    renderCliReference(),
    renderContributing(),
    renderCredits(),
    renderCrossLink(),
    renderFooter(),
  ]);
}

// ---- hero ----------------------------------------------------------

function renderHero(): HTMLElement {
  return h("section", { class: "fp-hero" },
    h("div", { class: "container" }, [
      h("div", { class: "fp-hero__copy" }, [
        h("span", { class: "hero-eyebrow" }, "OPEN SOURCE · MIT LICENSED"),
        h("h1", { class: "fp-hero__title" }, "flpdiff"),
        h("p", { class: "fp-hero__sub" }, "Semantic diff for FL Studio projects. Free, open source, runs in your terminal or browser."),
        h("div", { class: "fp-hero__cta" }, [
          renderCopyableCommand("npx flpdiff a.flp b.flp"),
          h("div", { class: "fp-hero__links" }, [
            h("a", { href: GITHUB_URL, target: "_blank", rel: "noopener", class: "fp-link" }, "View on GitHub →"),
            h("a", { href: "/flpdiff#viewer", class: "fp-link" }, "Try in browser →"),
          ]),
        ]),
      ]),
      h("div", { class: "fp-hero__demo" }, renderDiffCard()),
    ]),
  );
}

function renderDiffCard(): HTMLElement {
  return h("div", { class: "diff-card" }, [
    h("div", { class: "diff-card-head" }, [
      h("span", { class: "tag" }, "track_v4.flp"),
      h("span", { class: "arrow" }, "→"),
      h("span", { class: "tag new" }, "track_v5_FINAL.flp"),
      h("span", { class: "count" }, "23 changes"),
    ]),
    h("div", { class: "diff-card-body" }, [
      diffLine("ch", "~", ["Tempo 140.0 ", diffArrow(), " 145.0 BPM"]),
      diffLine("add", "+", ['Channel "Pad Synth" (Serum)']),
      diffLine("ch", "~", ['Channel "Lead" volume 78% ', diffArrow(), " 85%"]),
      diffLine("rm", "−", ['Channel "FX Hit 2"']),
      diffLine("ch", "~", ['Pattern 3 "Drums" — 4 notes added, 2 velocity changes']),
      diffLine("ch", "~", ['Pattern 7 "Bass" — 1 note removed, 3 notes shifted']),
      diffLine("ch", "~", ["Insert 2 · Fruity EQ 2 — Band 3 freq 2.4k ", diffArrow(), " 3.1kHz"]),
      diffLine("add", "+", ['Insert 5 · Serum preset "BA Growl Monster"']),
      diffLine("ch", "~", ["Arrangement track 2 — 3 clips repositioned"]),
      h("div", { class: "diff-note" }, "+ 14 more changes across mixer and arrangement"),
    ]),
  ]);
}

function diffLine(kind: "add" | "rm" | "ch", sign: string, body: (Node | string)[]): HTMLElement {
  return h("div", { class: `diff-line ${kind}` }, [
    h("span", { class: "sign" }, sign),
    h("span", null, body),
  ]);
}

function diffArrow(): HTMLElement {
  return h("span", { class: "diff-arrow" }, "→");
}

// ---- quick start ---------------------------------------------------

function renderQuickstart(): HTMLElement {
  return h("section", { class: "fp-quickstart", id: "quickstart" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "QUICK START"),
        h("h2", { class: "section-title" }, [
          "USE ON ",
          h("span", { class: "underline" }, "YOUR COMPUTER"),
          ".",
        ]),
      ]),
      h("ol", { class: "fp-steps" }, [
        h("li", { class: "fp-step" }, [
          h("div", { class: "fp-step__num" }, "01"),
          h("div", { class: "fp-step__body" }, [
            h("h3", null, "No install needed"),
            renderCopyableCommand("npx flpdiff a.flp b.flp"),
          ]),
        ]),
        h("li", { class: "fp-step" }, [
          h("div", { class: "fp-step__num" }, "02"),
          h("div", { class: "fp-step__body" }, [
            h("h3", null, "Or install globally"),
            renderCopyableCommand("npm install -g flpdiff\nflpdiff a.flp b.flp"),
          ]),
        ]),
        h("li", { class: "fp-step" }, [
          h("div", { class: "fp-step__num" }, "03"),
          h("div", { class: "fp-step__body" }, [
            h("h3", null, "Or use it in the browser"),
            h("p", null, [
              "Drop two .flp files in ",
              h("a", { href: "#viewer", class: "fp-link" }, "the viewer below"),
              ". Files parse locally — nothing uploads.",
            ]),
          ]),
        ]),
      ]),
    ]),
  );
}

// ---- embedded viewer ----------------------------------------------

/**
 * Inline drop-zone + diff slots. main.ts mounts this page once per
 * route entry, then `renderViewer(state)` populates these slots on
 * every state change. IDs match what main.ts queries.
 */
function renderViewerEmbed(): HTMLElement {
  return h("section", { class: "fp-viewer-section", id: "viewer" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "TRY IT"),
        h("h2", { class: "section-title" }, [
          "TRY IT IN ",
          h("span", { class: "underline" }, "YOUR BROWSER"),
          ".",
        ]),
      ]),
      h("p", { class: "section-lead" }, "Drop two .flp files. The diff renders inline. Files parse locally — nothing uploads to a server."),
      h("div", { class: "fp-viewer" }, [
        h("section", { id: "dropzone-slot", class: "dropzone-slot" }),
        h("section", { id: "error-slot", class: "error-slot", "aria-live": "polite" }),
        h("section", { id: "info-slot", class: "info-slot", "aria-live": "polite" }),
        h("section", { id: "diff-slot", class: "diff-slot", "aria-live": "polite" }),
      ]),
    ]),
  );
}

// ---- example output ----------------------------------------------

function renderExampleOutput(): HTMLElement {
  return h("section", { class: "fp-example" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "EXAMPLE OUTPUT"),
        h("h2", { class: "section-title" }, [
          "WHAT YOU ",
          h("span", { class: "underline" }, "GET"),
          ".",
        ]),
      ]),
      h("div", { class: "terminal terminal--example" }, [
        tline([tp("$"), " ", tc("npx flpdiff track_v4.flp track_v5_FINAL.flp")]),
        tline([""]),
        tline([td("23 changes across 4 categories")]),
        tline([""]),
        tline([td("Metadata:")]),
        tline([h("span", { class: "yellow" }, "  ~ Tempo 140.0 → 145.0 BPM")]),
        tline([""]),
        tline([td("Channels:")]),
        tline([h("span", { class: "green" }, "  + \"Pad Synth\" (Serum)")]),
        tline([h("span", { class: "yellow" }, "  ~ \"Lead\" volume 78% → 85%, pan 0 → -12")]),
        tline([h("span", { class: "red" }, "  − \"FX Hit 2\"")]),
        tline([""]),
        tline([td("Patterns:")]),
        tline([h("span", { class: "yellow" }, "  ~ Pattern 3 \"Drums\" — 4 notes added, 2 velocity changes")]),
        tline([h("span", { class: "yellow" }, "  ~ Pattern 7 \"Bass\" — 1 note removed, 3 notes shifted")]),
        tline([""]),
        tline([td("Mixer:")]),
        tline([h("span", { class: "yellow" }, "  ~ Insert 2 · Fruity EQ 2 — Band 3 freq 2.4k → 3.1kHz")]),
        tline([h("span", { class: "green" }, "  + Insert 5 · Serum preset \"BA Growl Monster\"")]),
        tline([""]),
        tline([td("Arrangement:")]),
        tline([h("span", { class: "yellow" }, "  ~ Track 2 — 3 clips repositioned")]),
      ]),
    ]),
  );
}

function tline(children: (Node | string)[]): HTMLElement {
  return h("div", null, children.length ? children : " ");
}
function tp(s: string): HTMLElement { return h("span", { class: "prompt" }, s); }
function tc(s: string): HTMLElement { return h("span", { class: "cmd" }, s); }
function td(s: string): HTMLElement { return h("span", { class: "dim" }, s); }

// ---- git integration ---------------------------------------------

function renderGitIntegration(): HTMLElement {
  return h("section", { class: "fp-git" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "GIT INTEGRATION"),
        h("h2", { class: "section-title" }, [
          "USE IT WITH ",
          h("span", { class: "underline" }, "GIT"),
          ".",
        ]),
      ]),
      h("p", { class: "section-lead" }, [
        "flpdiff ships with a git diff driver. Configure once and ",
        h("code", null, "git diff"),
        " on .flp files shows actual changes instead of “Binary files differ.”",
      ]),
      h("div", { class: "fp-code-block" }, [
        h("div", { class: "fp-code-block__label" }, ".gitconfig"),
        h("pre", null, h("code", null, "[diff \"flp\"]\n  command = flpdiff")),
      ]),
      h("div", { class: "fp-code-block" }, [
        h("div", { class: "fp-code-block__label" }, ".gitattributes"),
        h("pre", null, h("code", null, "*.flp diff=flp")),
      ]),
    ]),
  );
}

// ---- plugin matrix -----------------------------------------------

interface PluginRow {
  plugin: string;
  state: string;
  preset: string;
  notes: string;
}

const PLUGINS: PluginRow[] = [
  { plugin: "Fruity EQ 2",       state: "✅", preset: "—",  notes: "All bands" },
  { plugin: "Fruity Limiter",    state: "✅", preset: "—",  notes: "" },
  { plugin: "Serum",             state: "✅", preset: "✅", notes: "Preset name + parameters" },
  { plugin: "Vital",             state: "🔄", preset: "🔄", notes: "In progress" },
  { plugin: "FabFilter Pro-Q 3", state: "🔜", preset: "🔜", notes: "Help wanted" },
  { plugin: "Other VSTs",        state: "⚠️", preset: "⚠️", notes: "Detected as “settings changed” without details" },
];

function renderPluginMatrix(): HTMLElement {
  return h("section", { class: "fp-matrix" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "PLUGIN SUPPORT"),
        h("h2", { class: "section-title" }, [
          "SUPPORTED ",
          h("span", { class: "underline" }, "PLUGINS"),
          ".",
        ]),
      ]),
      h("p", { class: "section-lead" }, "flpdiff parses common plugin states. More plugins are added through community contributions."),
      h("div", { class: "fp-table-wrap" },
        h("table", { class: "fp-table" }, [
          h("thead", null, h("tr", null, [
            h("th", null, "Plugin"),
            h("th", null, "State"),
            h("th", null, "Preset"),
            h("th", null, "Notes"),
          ])),
          h("tbody", null,
            PLUGINS.map((p) => h("tr", null, [
              h("td", null, p.plugin),
              h("td", { class: "fp-table__cell--icon" }, p.state),
              h("td", { class: "fp-table__cell--icon" }, p.preset),
              h("td", { class: "fp-table__notes" }, p.notes),
            ])),
          ),
        ]),
      ),
      h("p", { class: "section-foot" }, [
        "Contributing a plugin parser is the most impactful way to help — see the ",
        h("a", { href: `${GITHUB_URL}/blob/main/CONTRIBUTING.md`, target: "_blank", rel: "noopener", class: "fp-link" }, "contribution guide on GitHub"),
        ".",
      ]),
    ]),
  );
}

// ---- CLI reference -----------------------------------------------

interface CliFlag {
  cmd: string;
  desc: string;
}

const FLAGS: CliFlag[] = [
  { cmd: "flpdiff <file1> <file2>", desc: "Basic diff." },
  { cmd: "flpdiff <file1> <file2> --json", desc: "Machine-readable output." },
  { cmd: "flpdiff <file1> <file2> --section channels", desc: "Filter to a specific section." },
];

function renderCliReference(): HTMLElement {
  return h("section", { class: "fp-cli-ref" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "CLI REFERENCE"),
        h("h2", { class: "section-title" }, [
          "FLAGS AND ",
          h("span", { class: "underline" }, "COMMANDS"),
          ".",
        ]),
      ]),
      h("dl", { class: "fp-flags" },
        FLAGS.flatMap((f) => [
          h("dt", null, h("code", null, f.cmd)),
          h("dd", null, f.desc),
        ]),
      ),
      h("p", { class: "section-foot" }, [
        h("a", { href: `${GITHUB_URL}#readme`, target: "_blank", rel: "noopener", class: "fp-link" }, "Full reference on GitHub →"),
      ]),
    ]),
  );
}

// ---- contributing ------------------------------------------------

function renderContributing(): HTMLElement {
  return h("section", { class: "fp-contrib" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "CONTRIBUTE"),
        h("h2", { class: "section-title" }, [
          "HELP MAKE ",
          h("span", { class: "underline" }, "FLPDIFF"),
          " BETTER.",
        ]),
      ]),
      h("p", { class: "section-lead" }, "flpdiff is open source under MIT. Contributions welcome — especially plugin parsers and bug reports against your own projects."),
      h("div", { class: "fp-cta-row" }, [
        h("a", { href: GITHUB_URL, target: "_blank", rel: "noopener", class: "btn btn-primary" }, "Star on GitHub"),
        h("a", { href: `${GITHUB_URL}/blob/main/CONTRIBUTING.md`, target: "_blank", rel: "noopener", class: "btn btn-secondary" }, "Contribution guide"),
        h("a", { href: `${GITHUB_URL}/issues`, target: "_blank", rel: "noopener", class: "btn btn-secondary" }, "Open issues"),
      ]),
    ]),
  );
}

// ---- credits -----------------------------------------------------

function renderCredits(): HTMLElement {
  return h("section", { class: "fp-credits" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "CREDITS"),
        h("h2", { class: "section-title" }, [
          "BUILT WITH ",
          h("span", { class: "underline" }, "GRATITUDE"),
          ".",
        ]),
      ]),
      h("div", { class: "fp-credits__body" }, [
        h("p", null, "flpdiff is MIT licensed. Built from scratch in TypeScript by Roman Pronskiy."),
        h("p", null, [
          "Special thanks to demberto and the ",
          h("a", { href: "https://github.com/demberto/PyFLP", target: "_blank", rel: "noopener", class: "fp-link" }, "PyFLP"),
          " project — their work on understanding the .flp format was foundational. flpdiff was rewritten in TypeScript for browser support, but PyFLP saved months of reverse engineering. If you need an FLP parser in Python, use PyFLP.",
        ]),
        h("p", { class: "fp-credits__npm" }, [
          h("a", { href: NPM_URL, target: "_blank", rel: "noopener", class: "fp-link" }, "npm"),
          " · ",
          h("a", { href: GITHUB_URL, target: "_blank", rel: "noopener", class: "fp-link" }, "github"),
        ]),
      ]),
    ]),
  );
}

// ---- cross-link to dawhub ----------------------------------------

function renderCrossLink(): HTMLElement {
  return h("section", { class: "fp-cross" },
    h("div", { class: "container" }, [
      h("div", { class: "fp-cross__inner" }, [
        h("h2", null, [
          "LOOKING FOR ",
          h("span", { class: "accent-box" }, "COLLABORATION"),
          " FEATURES?",
        ]),
        h("p", null, "flpdiff is the open-source diff engine. dawhub adds version history, AI integration, sharing, and collaboration — built for producers who work in teams or want a complete project workflow."),
        h("a", { href: "/", class: "btn btn-primary" }, "Learn more about dawhub →"),
      ]),
    ]),
  );
}

// ---- copyable command --------------------------------------------

function renderCopyableCommand(cmd: string): HTMLElement {
  const wrap = h("div", { class: "fp-cmd" }, [
    h("pre", null, h("code", null, cmd)),
  ]);
  const btn = h("button", {
    type: "button",
    class: "fp-cmd__copy",
    "aria-label": "Copy command to clipboard",
    onclick: async (ev: Event) => {
      const target = ev.currentTarget as HTMLButtonElement;
      try {
        await navigator.clipboard.writeText(cmd);
        const original = target.textContent;
        target.textContent = "COPIED";
        target.classList.add("fp-cmd__copy--ok");
        setTimeout(() => {
          target.textContent = original;
          target.classList.remove("fp-cmd__copy--ok");
        }, 1400);
      } catch {
        target.textContent = "FAILED";
        setTimeout(() => { target.textContent = "COPY"; }, 1400);
      }
    },
  }, "COPY");
  wrap.appendChild(btn);
  return wrap;
}
